/**
 * The per-package toggles on the Registry page (plans/2026-10-14-publish-
 * toggles.md): type profile, LLVM PGO, text order — and "Tests first"
 * (internal/publish plans/2026-10-19-testing-first.md): ON (default) runs
 * the package + full-project tests before the addon build and gates the
 * publish on them; OFF runs them after the publish and the benchmark.
 *
 * The broker (private/auth publishToggleModel.ts) resolves each package's
 * overrides against the publish host's global defaults and returns the result
 * as `settings` on every package of GET /v1/publish/packages; this file shapes
 * that for the page — one row per toggle with its choice, the global default,
 * the effective state and WHY (a toggle that is effectively off says
 * "off (operator)" or "off (default)", never a bare "off") — and parses the
 * settings a run resolved at its start (`publish_versions.settings`) so the
 * run's checklist can say what it used and whether the package has changed
 * since.
 */

export type SettingKey =
  "typeProfile" | "llvmPgo" | "textOrder" | "testingFirst";
export type SettingOverride = "on" | "off" | "inherit";

export const SETTING_KEYS: readonly SettingKey[] = [
  "typeProfile",
  "llvmPgo",
  "textOrder",
  "testingFirst",
];

export interface ToggleView {
  override: SettingOverride;
  default: boolean | null;
  effective: boolean | null;
  label: string | null;
}

/** `settings` of a package in GET /v1/publish/packages. */
export interface SettingsView {
  typeProfile: ToggleView;
  llvmPgo: ToggleView;
  textOrder: ToggleView;
  /** Absent from a broker that predates the setting (treated as unknown). */
  testingFirst?: ToggleView;
  runsInstrumented: boolean | null;
  orderOnly: boolean | null;
  notes: string[];
}

export const TOGGLE_TITLE: Record<SettingKey, string> = {
  typeProfile: "Type profile",
  llvmPgo: "LLVM PGO",
  textOrder: "Text order",
  testingFirst: "Tests first",
};

const TOGGLE_HELP: Record<SettingKey, string> = {
  typeProfile:
    "Collection compile + local training run; the final build consumes the collected type profile.",
  llvmPgo:
    "Instrumented compile + LLVM training run; the final build is built with --pgo-use.",
  textOrder:
    "The package's .text order, derived from the LLVM-instrumented training run and linked into the final build.",
  testingFirst:
    "Run package + full-project tests first (default). Off: run them after publish and benchmark; publish is no longer gated on them.",
};

export interface ToggleRow {
  key: SettingKey;
  title: string;
  help: string;
  /** The operator's choice (the select's value). */
  override: SettingOverride;
  /** "Default (on)" / "Default (off)" / "Default (unknown)" — the inherit
   *  option's text, so the global default is always visible. */
  inheritLabel: string;
  /** What a publish/rebuild would do now: "on (operator)", "off (default)"… */
  effectiveLabel: string;
  effective: boolean | null;
  /** Why this toggle is effectively off ("off (operator)" / "off (default)"),
   *  or null when it is on or unknown. */
  disabledReason: string | null;
  /** A dependency note that applies to this toggle in the current
   *  combination (e.g. text order on with LLVM PGO off = order only). */
  dependencyNote: string | null;
}

function defaultText(d: boolean | null): string {
  return d === null ? "unknown" : d ? "on" : "off";
}

/** Rows for the three toggles of one package. */
export function toggleRows(s: SettingsView): ToggleRow[] {
  return SETTING_KEYS.filter((key) => s[key] !== undefined).map((key) => {
    const t = s[key]!;
    const known = t.effective !== null;
    let dependencyNote: string | null = null;
    if (key === "textOrder" && t.effective === true && s.orderOnly === true)
      dependencyNote =
        "LLVM PGO is off: order only. The instrumented build and training still run to derive the order; the final build is not built with --pgo-use.";
    else if (
      key === "llvmPgo" &&
      t.effective === false &&
      s.runsInstrumented === true
    )
      dependencyNote =
        "Not applied (order only): the instrumented build still runs because text order is on.";
    else if (
      key === "llvmPgo" &&
      t.effective === true &&
      s.textOrder.effective !== true
    )
      dependencyNote = "Needs the instrumented build and training run.";
    else if (
      key === "textOrder" &&
      t.effective === false &&
      s.runsInstrumented === false
    )
      dependencyNote =
        "LLVM PGO is off too: the instrumented build and LLVM training are skipped.";
    else if (key === "testingFirst" && t.effective === false)
      dependencyNote =
        "The package test and the full-project test run after the publish and the benchmark; the publish is not gated on them. The addon verify (addon vs node) still gates the publish. A failure afterwards is flagged on the published build and never unpublishes it.";
    return {
      key,
      title: TOGGLE_TITLE[key],
      help: TOGGLE_HELP[key],
      override: t.override,
      inheritLabel: `Default (${defaultText(t.default)})`,
      effectiveLabel: t.label ?? "unknown",
      effective: t.effective,
      disabledReason: known && t.effective === false ? t.label : null,
      dependencyNote,
    };
  });
}

/** The explanation shown once for the package: the dependency notes plus the
 *  rebuild hint (settings apply at the next publish/rebuild, not retroactively). */
export function settingsFooter(s: SettingsView): string[] {
  return [
    ...s.notes,
    "Changes apply to the next publish or rebuild of a version — use Rebuild to publish a new build with the current settings. A run already in progress keeps the settings it started with.",
  ];
}

/** Body of PUT /v1/publish/packages/:name/settings for changing one toggle. */
export function settingChange(
  key: SettingKey,
  to: SettingOverride,
): Record<string, SettingOverride> {
  return { [key]: to };
}

export function parseOverrideValue(v: string): SettingOverride | null {
  return v === "on" || v === "off" || v === "inherit" ? v : null;
}

// ── The settings a run resolved (publish_versions.settings) ──────────────

export interface RunToggle {
  on: boolean;
  source: "operator" | "default";
  label: string;
}

export interface RunSettings {
  typeProfile: RunToggle;
  llvmPgo: RunToggle;
  textOrder: RunToggle;
  /** Testing first the run resolved (a run from before the setting: on). */
  testingFirst: RunToggle;
  /** The run pinned "tests last": the package + full-project tests run
   *  after the publish and the benchmark. */
  testsLast: boolean;
  runsInstrumented: boolean;
  appliesPgoUse: boolean;
  orderOnly: boolean;
}

function parseToggle(x: unknown): RunToggle | null {
  if (!x || typeof x !== "object") return null;
  const t = x as Record<string, unknown>;
  if (typeof t.on !== "boolean" || typeof t.label !== "string") return null;
  return {
    on: t.on,
    source: t.source === "operator" ? "operator" : "default",
    label: t.label,
  };
}

/** Defensive parse of a version row's `settings` (JSON string, object, or
 *  null). null = the run predates the toggles. */
export function parseRunSettings(raw: unknown): RunSettings | null {
  let v: unknown = raw;
  if (typeof raw === "string") {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const typeProfile = parseToggle(o.typeProfile);
  const llvmPgo = parseToggle(o.llvmPgo);
  const textOrder = parseToggle(o.textOrder);
  if (!typeProfile || !llvmPgo || !textOrder) return null;
  const testingFirst = parseToggle(o.testingFirst) ?? {
    on: true,
    source: "default" as const,
    label: "on (default)",
  };
  return {
    typeProfile,
    llvmPgo,
    textOrder,
    testingFirst,
    testsLast:
      typeof o.testsLast === "boolean" ? o.testsLast : !testingFirst.on,
    runsInstrumented:
      typeof o.runsInstrumented === "boolean"
        ? o.runsInstrumented
        : llvmPgo.on || textOrder.on,
    appliesPgoUse:
      typeof o.appliesPgoUse === "boolean" ? o.appliesPgoUse : llvmPgo.on,
    orderOnly:
      typeof o.orderOnly === "boolean"
        ? o.orderOnly
        : textOrder.on && !llvmPgo.on,
  };
}

/** "Type profile off (operator) · LLVM PGO not applied (order only) · Text
 *  order on (default)" — the line on the run's step checklist. */
export function runSettingsLine(r: RunSettings): string {
  const llvm = r.orderOnly ? "not applied (order only)" : r.llvmPgo.label;
  return [
    `Type profile ${r.typeProfile.label}`,
    `LLVM PGO ${llvm}`,
    `Text order ${r.textOrder.label}`,
    // Only a tests-last run says so: the default (tests first) line is the
    // line it always was.
    ...(r.testsLast
      ? [`Tests first ${r.testingFirst.label}: tests ran after the publish`]
      : r.testingFirst.on
        ? []
        : [`Tests first ${r.testingFirst.label} (verify-only run: ran first)`]),
  ].join(" · ");
}

/** The toggles whose current effective state differs from what the run used
 *  — drives the "rebuild with current settings" hint. Unknown current states
 *  never count as a difference. */
export function settingsDrift(
  run: RunSettings | null,
  current: SettingsView | null | undefined,
): SettingKey[] {
  if (!run || !current) return [];
  const out: SettingKey[] = [];
  for (const key of SETTING_KEYS) {
    const eff = current[key]?.effective ?? null;
    if (eff !== null && eff !== run[key].on) out.push(key);
  }
  return out;
}

// ── The deferred tests of a "tests last" run (publish_versions.
// tests_after_publish; internal/publish types.ts TestsAfterPublish) ───────

export type TestsAfterPublishState =
  "pending" | "running" | "passed" | "failed" | "interrupted" | "skipped";

export interface TestsAfterPublish {
  order: "last";
  state: TestsAfterPublishState;
  failedStep?: string;
  error?: string;
  publishedAs?: string;
  startedAt?: number;
  endedAt?: number;
  attempts?: number;
}

const TESTS_STATES: readonly string[] = [
  "pending",
  "running",
  "passed",
  "failed",
  "interrupted",
  "skipped",
];

/** Defensive parse of a version row's `tests_after_publish` (JSON string,
 *  object, or null). null = the run tested first / never published. */
export function parseTestsAfterPublish(raw: unknown): TestsAfterPublish | null {
  let v: unknown = raw;
  if (typeof raw === "string") {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (o.order !== "last" || typeof o.state !== "string") return null;
  if (!TESTS_STATES.includes(o.state)) return null;
  return o as unknown as TestsAfterPublish;
}

export interface TestsBadge {
  label: string;
  /** Badge variant: failed is loud, unfinished is calm. */
  variant: "destructive" | "warning" | "secondary" | "success";
  title: string;
}

/** The badge a published build carries while / after its deferred tests:
 *  null when the run tested first or the tests passed (nothing to flag).
 *  A FAILED verdict is the "tests failed after publish" flag. */
export function testsAfterPublishBadge(
  t: TestsAfterPublish | null,
): TestsBadge | null {
  if (!t) return null;
  switch (t.state) {
    case "failed":
      return {
        label: "tests failed after publish",
        variant: "destructive",
        title: `The build is published (this run did not gate on the package/full-project tests), but those tests FAILED${
          t.failedStep ? ` at ${t.failedStep}` : ""
        }${t.error ? `:\n${t.error}` : "."}\nThe build is NOT withdrawn — use Withdraw if it should come down.`,
      };
    case "running":
      return {
        label: "tests running",
        variant: "secondary",
        title:
          "Published. The package and full-project tests are running now (testing first is off for this run).",
      };
    case "pending":
      return {
        label: "tests pending",
        variant: "secondary",
        title:
          "Published. The package and full-project tests run after the benchmark (testing first is off for this run).",
      };
    case "interrupted":
      return {
        label: "tests interrupted",
        variant: "warning",
        title:
          "Published. pure-publish restarted before the package/full-project tests finished; they are resumed automatically (or Republish).",
      };
    case "skipped":
      return {
        label: "tests skipped",
        variant: "warning",
        title: `Published, but the package/full-project tests did not run${t.error ? `: ${t.error}` : "."}`,
      };
    case "passed":
      return null;
  }
}

export function driftHint(drift: readonly SettingKey[]): string | null {
  if (drift.length === 0) return null;
  return `This package's current ${drift
    .map((k) => TOGGLE_TITLE[k])
    .join(
      ", ",
    )} setting differs from what this run used. Rebuild to publish a build with the current settings.`;
}
